import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Link,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  FormHelperText,
  InputAdornment,
  IconButton,
  Checkbox,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useForm, SubmitHandler } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  signUpTalent,
  signUpCompany,
  sendVerificationEmailTalent,
  sendVerificationEmailCompany,
  clearError,
} from "src/features/authSlice";
import { RootState, AppDispatch } from "src/store";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import PasswordStrengthChecker from "src/components/PasswordStrengthChecker";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { AccountType } from "src/types/firebase";
import { useTranslation, Trans } from "react-i18next";

// Form Interfaces for each account type
interface TalentSignupFormInputs {
  referenceCode: string;
  fullname: string;
  email: string;
  password: string;
  countryCode: string;
  countryName: string;
  phone: string;
  location: string;
  agreeToTerms: boolean;
}

interface CompanySignupFormInputs {
  fullname: string;
  email: string;
  password: string;
  companyName: string;
  companyWebsite: string;
  agreeToTerms: boolean;
}

interface BackofficeSignupFormInputs {
  fullname: string;
  email: string;
  password: string;
  agreeToTerms: boolean;
}

type SignUpFormInputs =
  | TalentSignupFormInputs
  | CompanySignupFormInputs
  | BackofficeSignupFormInputs;

const personalEmailDomains = [
  "gmail.com",
  "hotmail.com",
  "yahoo.com",
  "outlook.com",
  "live.com",
  "aol.com",
  "icloud.com",
  "mail.com",
];

const companyEmailTest = (value: string) => {
  const domain = value.split("@")[1];
  return !personalEmailDomains.includes(domain);
};

const companyWebsiteTest = (value: string) => {
  const urlPattern = new RegExp(
    "^(https?:\\/\\/)?" +
      "((([a-zA-Z\\d]([a-zA-Z\\d-]*[a-zA-Z\\d])*)\\.?)+[a-zA-Z]{2,}|" +
      "((\\d{1,3}\\.){3}\\d{1,3}))" +
      "(\\:\\d+)?(\\/[-a-zA-Z\\d%_.~+]*)*" +
      "(\\?[;&a-zA-Z\\d%_.~+=-]*)?" +
      "(\\#[-a-zA-Z\\d_]*)?$",
    "i"
  );
  return urlPattern.test(value);
};

const countryCodes = [
  { code: "+1", name: "USA/Canada" },
  { code: "+44", name: "UK" },
  { code: "+49", name: "Germany" },
  { code: "+90", name: "Turkey" },
  { code: "+91", name: "India" },
  { code: "+86", name: "China" },
  { code: "+81", name: "Japan" },
  { code: "+33", name: "France" },
  { code: "+39", name: "Italy" },
  { code: "+34", name: "Spain" },
];

const countries = [
  "Turkey",
  "United States",
  "United Kingdom",
  "Germany",
  "France",
  "India",
  "China",
  "Japan",
  "Italy",
  "Spain",
];

const SignUpPage: React.FC = () => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [selectedAccountType, setSelectedAccountType] =
    useState<AccountType>("talent");

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  // Memoize schema - only recreate when language or account type changes
  const schema = useMemo(
    () => {
      if (selectedAccountType === "talent") {
        return Yup.object().shape({
          referenceCode: Yup.string()
            .required(t("auth.validation.referenceCode"))
            .min(3, t("auth.validation.referenceCodeMinLength")),
          fullname: Yup.string().required(t("auth.validation.fullName")),
          email: Yup.string()
            .email(t("auth.validation.invalidEmail"))
            .required(t("auth.validation.email")),
          password: Yup.string()
            .required(t("auth.validation.password"))
            .min(6, t("auth.validation.passwordMinLength", { count: 6 })),
          countryCode: Yup.string().required(t("auth.validation.countryCode")),
          countryName: Yup.string().required(t("auth.validation.country")),
          phone: Yup.string().required(t("auth.validation.phone")),
          location: Yup.string().required(t("auth.validation.location")),
          agreeToTerms: Yup.bool()
            .oneOf([true], t("auth.validation.agreeToTerms"))
            .required(t("auth.validation.agreeToTerms")),
        });
      } else if (selectedAccountType === "company") {
        return Yup.object().shape({
          fullname: Yup.string().required(t("auth.validation.fullName")),
          email: Yup.string()
            .email(t("auth.validation.invalidEmail"))
            .required(t("auth.validation.workEmail"))
            .test(
              "is-company-email",
              t("auth.validation.useCompanyEmail"),
              companyEmailTest
            ),
          password: Yup.string()
            .required(t("auth.validation.password"))
            .min(6, t("auth.validation.passwordMinLength", { count: 6 })),
          companyName: Yup.string().required(t("auth.validation.companyName")),
          companyWebsite: Yup.string()
            .required(t("auth.validation.companyWebsite"))
            .test("is-valid-url", t("auth.validation.invalidUrl"), companyWebsiteTest),
          agreeToTerms: Yup.bool()
            .oneOf([true], t("auth.validation.agreeToTerms"))
            .required(t("auth.validation.agreeToTerms")),
        });
      } else {
        return Yup.object().shape({
          fullname: Yup.string().required(t("auth.validation.fullName")),
          email: Yup.string()
            .email(t("auth.validation.invalidEmail"))
            .required(t("auth.validation.email")),
          password: Yup.string()
            .required(t("auth.validation.password"))
            .min(6, t("auth.validation.passwordMinLength", { count: 6 })),
          agreeToTerms: Yup.bool()
            .oneOf([true], t("auth.validation.agreeToTerms"))
            .required(t("auth.validation.agreeToTerms")),
        });
      }
    },
    [selectedAccountType, t]
  );
  const resolver = useMemo(() => yupResolver(schema), [schema]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<SignUpFormInputs>({
    resolver: resolver as any,
    defaultValues: {
      agreeToTerms: false,
      countryCode: "+90",
      countryName: "Turkey",
    },
  });

  const dispatch = useDispatch<AppDispatch>();
  const { loading, error } = useSelector(
    (state: RootState) => state.auth
  );
  const navigate = useNavigate();

  useEffect(() => {
    // Clear error when component unmounts or account type changes
    return () => {
      dispatch(clearError());
    };
  }, [dispatch, selectedAccountType]);

  const onSubmit: SubmitHandler<SignUpFormInputs> = async (data) => {
    dispatch(clearError());

    if (selectedAccountType === "backoffice") {
      // Backoffice signup is not implemented yet
      return;
    }

    const navigateToVerification = (
      accountType: "talent" | "company",
      userId: string,
      email: string,
      syncCode?: string
    ) => {
      const params = new URLSearchParams({
        accountType,
        userId,
        email,
      });

      if (syncCode) {
        params.set("syncCode", syncCode);
      }

      navigate(`/verify-email?${params.toString()}`);
    };

    if (selectedAccountType === "talent") {
      const talentData = data as TalentSignupFormInputs;
      const processedData = {
        ...talentData,
        isSocial: false,
        deviceId: `web-${Date.now()}`,
      };
      const signupResult = await dispatch(signUpTalent(processedData));

      if (signUpTalent.fulfilled.match(signupResult)) {
        const createdUser = signupResult.payload.user;

        if (!createdUser?.userId || !createdUser?.email) {
          return;
        }

        const verificationResult = await dispatch(
          sendVerificationEmailTalent(createdUser)
        );

        if (sendVerificationEmailTalent.fulfilled.match(verificationResult)) {
          navigateToVerification(
            "talent",
            createdUser.userId,
            createdUser.email,
            verificationResult.payload?.syncCode
          );
        }
      }
    } else if (selectedAccountType === "company") {
      const companyData = data as CompanySignupFormInputs;
      const signupResult = await dispatch(signUpCompany(companyData));

      if (signUpCompany.fulfilled.match(signupResult)) {
        const createdUser = signupResult.payload.user;

        if (!createdUser?.userId || !createdUser?.email) {
          return;
        }

        const verificationResult = await dispatch(
          sendVerificationEmailCompany(createdUser)
        );

        if (sendVerificationEmailCompany.fulfilled.match(verificationResult)) {
          navigateToVerification(
            "company",
            createdUser.userId,
            createdUser.email,
            verificationResult.payload?.syncCode
          );
        }
      }
    }
  };

  const handleAccountTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setSelectedAccountType(value as AccountType);
    setValue("agreeToTerms", false);
  };

  return (
    <Grid container sx={{ minHeight: "100vh", p: [1, 4] }}>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          bgcolor: "primary.main",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          borderRadius: 5,
          boxShadow: 3,
          display: { xs: "none", md: "flex" },
          pb: 28,
          flexDirection: "column",
        }}
      >
        <Box>
          <img
            src="/logo.png"
            alt="Logo"
            style={{ height: 230, filter: "invert(1)" }}
          />
        </Box>
        <Typography variant="h4" align="center">
          {t("auth.signup.joinUs")}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img src="/logo.png" alt="Logo" style={{ height: 180 }} />
        </Box>
        <Box sx={{ maxWidth: 600, mx: "auto", width: "100%" }}>
          <Typography variant="h5" gutterBottom>
            {t("auth.signup.title")}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <RadioGroup
              value={selectedAccountType}
              onChange={handleAccountTypeChange}
              sx={{ mt: 2 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    value="talent"
                    control={<Radio size="small" sx={{ mt: -1 }} />}
                    sx={{
                      alignItems: "flex-start",
                      border:
                        selectedAccountType === "talent"
                          ? "2px solid primary.main"
                          : "2px solid #EEEEEE",
                      py: [2, 3],
                      pr: 1,
                      borderRadius: "10px",
                      mx: 0,
                      height: "100%",
                      width: "100%",
                    }}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={"bold"}>
                          {t("auth.signup.accountType.talent")}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t("auth.signup.accountType.talentDesc")}
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    value="company"
                    control={<Radio size="small" sx={{ mt: -1 }} />}
                    sx={{
                      alignItems: "flex-start",
                      border:
                        selectedAccountType === "company"
                          ? "2px solid primary.main"
                          : "2px solid #EEEEEE",
                      py: [2, 3],
                      pr: [1, 2],
                      borderRadius: "10px",
                      mx: 0,
                      height: "100%",
                      width: "100%",
                    }}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={"bold"}>
                          {t("auth.signup.accountType.company")}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t("auth.signup.accountType.companyDesc")}
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    value="backoffice"
                    control={<Radio size="small" sx={{ mt: -1 }} />}
                    sx={{
                      alignItems: "flex-start",
                      border:
                        selectedAccountType === "backoffice"
                          ? "2px solid primary.main"
                          : "2px solid #EEEEEE",
                      py: [2, 3],
                      pr: [1, 2],
                      borderRadius: "10px",
                      mx: 0,
                      height: "100%",
                      width: "100%",
                    }}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={"bold"}>
                          {t("auth.signup.accountType.backoffice")}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t("auth.signup.accountType.backofficeDesc")}
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              </Grid>
            </RadioGroup>

            {selectedAccountType === "backoffice" && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t("auth.signup.backofficeWarning")}
              </Alert>
            )}

            {selectedAccountType === "talent" && (
              <>
                <TextField
                  label={t("auth.signup.fields.referenceCode")}
                  fullWidth
                  margin="normal"
                  {...register("referenceCode" as any)}
                  error={!!(errors as any)?.referenceCode}
                  helperText={(errors as any)?.referenceCode?.message}
                  placeholder={t("auth.signup.placeholders.referenceCode")}
                />
              </>
            )}

            {selectedAccountType === "backoffice" && (
              <TextField
                label={t("auth.signup.fields.fullName")}
                fullWidth
                margin="normal"
                {...register("fullname" as any)}
                error={!!(errors as any)?.fullname}
                helperText={(errors as any)?.fullname?.message}
              />
            )}



            {selectedAccountType === "talent" && (
              <>
                <TextField
                  label={t("auth.signup.fields.fullName")}
                  fullWidth
                  margin="normal"
                  {...register("fullname" as any)}
                  error={!!(errors as any)?.fullname}
                  helperText={(errors as any)?.fullname?.message}
                />
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>{t("auth.signup.fields.countryCode")}</InputLabel>
                      <Select
                        label={t("auth.signup.fields.countryCode")}
                        defaultValue="+90"
                        {...register("countryCode" as any)}
                        error={!!(errors as any)?.countryCode}
                      >
                        {countryCodes.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.code} {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      label={t("auth.signup.fields.phone")}
                      fullWidth
                      margin="normal"
                      {...register("phone" as any)}
                      error={!!(errors as any)?.phone}
                      helperText={(errors as any)?.phone?.message}
                    />
                  </Grid>
                </Grid>
                <FormControl fullWidth margin="normal">
                  <InputLabel>{t("auth.signup.fields.country")}</InputLabel>
                  <Select
                    label={t("auth.signup.fields.country")}
                    defaultValue="Turkey"
                    {...register("countryName" as any)}
                    error={!!(errors as any)?.countryName}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={t("auth.signup.fields.location")}
                  fullWidth
                  margin="normal"
                  {...register("location" as any)}
                  error={!!(errors as any)?.location}
                  helperText={(errors as any)?.location?.message}
                  placeholder={t("auth.signup.placeholders.location")}
                />
              </>
            )}

            {selectedAccountType === "company" && (
              <>
                <TextField
                  label={t("auth.signup.fields.fullName")}
                  fullWidth
                  margin="normal"
                  {...register("fullname" as any)}
                  error={!!(errors as any)?.fullname}
                  helperText={(errors as any)?.fullname?.message}
                />
                <TextField
                  label={t("auth.signup.fields.companyName")}
                  fullWidth
                  margin="normal"
                  {...register("companyName" as any)}
                  error={!!(errors as any)?.companyName}
                  helperText={(errors as any)?.companyName?.message}
                />
                <TextField
                  label={t("auth.signup.fields.companyWebsite")}
                  fullWidth
                  margin="normal"
                  {...register("companyWebsite" as any)}
                  error={!!(errors as any)?.companyWebsite}
                  helperText={(errors as any)?.companyWebsite?.message}
                  placeholder={t("auth.signup.placeholders.companyWebsite")}
                />
              </>
            )}

            <TextField
              label={
                selectedAccountType === "company"
                  ? t("auth.signup.fields.workEmail")
                  : t("auth.signup.fields.email")
              }
              fullWidth
              margin="normal"
              {...register("email" as any)}
              error={!!(errors as any)?.email}
              helperText={(errors as any)?.email?.message}
              type="email"
            />
            {selectedAccountType === "company" && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  {t("auth.signup.companyEmailWarning")}
                </Typography>
              </Alert>
            )}
            <TextField
              label={t("auth.signup.fields.password")}
              fullWidth
              margin="normal"
              {...register("password" as any)}
              error={!!(errors as any)?.password}
              helperText={(errors as any)?.password?.message}
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {(watch("password") as any) && (
              <PasswordStrengthChecker password={watch("password") as any} />
            )}

            <FormControlLabel
              control={<Checkbox {...register("agreeToTerms" as any)} />}
              label={
                <Typography variant="body2">
                  <Trans
                    i18nKey="auth.signup.terms"
                    components={{
                      privacyPolicy: <Link href="/privacy-policy" target="_blank" />,
                      termsAndConditions: (
                        <Link href="/terms-and-conditions" target="_blank" />
                      ),
                    }}
                  />
                </Typography>
              }
            />
            {(errors as any)?.agreeToTerms && (
              <FormHelperText error>
                {(errors as any)?.agreeToTerms?.message}
              </FormHelperText>
            )}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading || selectedAccountType === "backoffice"}
              sx={{ mt: 2, fontWeight: "bold" }}
            >
              {loading
                ? t("auth.signup.signingUp")
                : selectedAccountType === "backoffice"
                ? t("auth.signup.notAvailable")
                : t("auth.signup.signUpButton")}
            </Button>
          </form>
          <Box mt={2}>
            <Typography variant="body2">
              {t("auth.signup.alreadyHaveAccount")}{" "}
              <Link href="/login" underline="none">
                {t("auth.signup.signIn")}
              </Link>
            </Typography>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SignUpPage;
